import { FC, useState } from 'react';
import { useVideoCallContext } from '../../../../../contexts/VideoCallProvider';
import { useVideoCallRoom } from '../../hooks/useVideoCallRoom';
import { usePictureInPicture } from '../../hooks/usePictureInPicture';
import { useVideoCallActions } from '../../hooks/useVideoCallActions';
import { VideoCall } from '../../../../../types/VideoCall';
import { VideoGrid } from '@new/common/components/video/VideoGrid/VideoGrid';
import { MediaControls } from '@new/common/components/video/MediaControls/MediaControls';
import { CallHeader } from '../CallHeader/CallHeader';
import { SidePanel } from '../SidePanel/SidePanel';
import { CourseHearingLayout } from '../CourseHearingLayout/CourseHearingLayout';
import { PermissionHelpPanel } from '../PermissionHelpPanel/PermissionHelpPanel';

export interface VideoCallModalProps {
  videoCall: VideoCall;
  twilioToken: string;
}

export const VideoCallModal: FC<VideoCallModalProps> = ({ videoCall, twilioToken }) => {
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(true);

  const { isConfirmed, isInstructor, closeVideoCall, setPhotos } = useVideoCallContext();

  const { room, media, devices } = useVideoCallRoom({
    twilioToken,
    videoCallId: videoCall.id.toString(),
    isConfirmed,
  });

  const pip = usePictureInPicture();

  const actions = useVideoCallActions({
    videoCall,
    closeVideoCall,
    setPhotos,
    enterPiP: () => pip.enterPiP(),
  });

  const studentName = `${videoCall.student?.firstName ?? ''} ${
    videoCall.student?.lastName ?? ''
  }`.trim();

  return (
    <CourseHearingLayout
      header={
        <CallHeader
          title={videoCall.course?.name || 'Video Call'}
          studentName={studentName || 'Unknown Student'}
          videoId={videoCall.id}
          timerDisplay="0:00 min"
          onMinimize={pip.isSupported ? actions.onMinimize : undefined}
        />
      }
      videoArea={
        <VideoGrid
          localParticipant={room.localParticipant}
          remoteParticipants={room.remoteParticipants}
          isSpeakerEnabled={media.state.isSpeakerEnabled}
          isConnecting={room.isConnecting}
          isInstructor={isInstructor}
          errorSlot={
            room.connectionError ? (
              <PermissionHelpPanel kind={room.connectionError} onRetry={room.retry} />
            ) : undefined
          }
        />
      }
      controls={
        <MediaControls
          mediaState={media.state}
          onToggleVideo={media.toggleVideo}
          onToggleAudio={media.toggleAudio}
          onToggleSpeaker={media.toggleSpeaker}
          onToggleScreenShare={media.toggleScreenShare}
          onHangUp={actions.onHangUp}
          onTakePhoto={actions.onTakePhoto}
          isInstructor={isInstructor}
          microphones={devices.microphones}
          activeMicId={devices.activeMicId}
          onSwitchMicrophone={devices.switchMicrophone}
          speakers={devices.speakers}
          activeSpeakerId={devices.activeSpeakerId}
          onSwitchSpeaker={devices.switchSpeaker}
          cameras={devices.cameras}
          activeCameraId={devices.activeCameraId}
          onSwitchCamera={devices.switchCamera}
        />
      }
      sidePanel={<SidePanel />}
      isSidePanelOpen={isSidePanelOpen}
      onToggleSidePanel={() => setIsSidePanelOpen(!isSidePanelOpen)}
      isInstructor={isInstructor}
      isPiPActive={pip.isPiP}
    />
  );
};
